import React from 'react'
import type { Campaign } from '@mobi/account/Areas/SignUp'
import {
  getButtonText,
  getExpiryDate,
  getRemainingAmount,
  getTermsAndConditions,
  getTitleText,
  getTotalAmount,
  shouldDisplayAmounts,
  shouldDisplayCampaign,
  shouldShowButton,
} from '@mobi/account/Areas/SignUp/Utils/BonusOffer'
import { colors } from '@mobi/component-library/Foundations'
import { Grid, GridCell } from '@mobi/component-library/Legacy/Grid'
import { trackBonusOfferClick } from '@core/Areas/Bonuses/analytics'
import {
  BetOfferContainerStyled,
  BonusAmountsHeadingStyled,
  BonusAmountsRowStyled,
  BonusAmountsValueStyled,
  BonusButtonCellStyled,
  BonusButtonRowStyled,
  BonusCellStyled,
  BonusDetailRowStyled,
  BonusExpiryDateStyled,
  BonusFinePrintRowStyled,
  BonusHeaderRowStyled,
  BonusHeaderTitle,
  BonusIconStyled,
  ButtonBlockStyled,
  TermsAndConditionsLinkStyled,
} from '../BonusesPage.styles'

interface BetOfferItemProps {
  campaign: Campaign
  handleCampaignClick: (campaign: Campaign) => void
}

export const BetOfferItem = ({
  campaign,
  handleCampaignClick,
}: BetOfferItemProps): JSX.Element | null => {
  if (!shouldDisplayCampaign(campaign)) {
    return null
  }

  const handleCtaClick = (campaign: Campaign) => {
    handleCampaignClick(campaign)
    trackBonusOfferClick(campaign, getButtonText(campaign))
  }

  return (
    <BetOfferContainerStyled data-tid-bonus-detail>
      <Grid>
        <BonusHeaderRowStyled data-tid-bonus-detail-heading>
          <BonusCellStyled valign='middle' align='left'>
            <BonusIconStyled name='LineGift01' size='1.6rem' color={colors.studio[700]} />
            <BonusHeaderTitle>{getTitleText(campaign)}</BonusHeaderTitle>
          </BonusCellStyled>

          <BonusExpiryDateStyled>{getExpiryDate(campaign)}</BonusExpiryDateStyled>
        </BonusHeaderRowStyled>
        <BonusDetailRowStyled data-tid-bonus-detail-description>
          <GridCell valign='middle' align='left'>
            {campaign.description}
          </GridCell>
        </BonusDetailRowStyled>

        {shouldDisplayAmounts(campaign) && (
          <BonusAmountsRowStyled data-tid-bonus-detail-amount>
            <GridCell valign='middle' align='left' flexGrow={1}>
              <BonusAmountsHeadingStyled>Total</BonusAmountsHeadingStyled>
              <BonusAmountsValueStyled>{getTotalAmount(campaign)}</BonusAmountsValueStyled>
            </GridCell>
            <GridCell valign='middle' align='left' flexGrow={1}>
              <BonusAmountsHeadingStyled>Remaining</BonusAmountsHeadingStyled>
              <BonusAmountsValueStyled>{getRemainingAmount(campaign)}</BonusAmountsValueStyled>
            </GridCell>
          </BonusAmountsRowStyled>
        )}
        <BonusFinePrintRowStyled data-tid-bonus-detail-fineprint>
          <GridCell valign='middle' align='left'>
            {getTermsAndConditions(campaign)}{' '}
            <TermsAndConditionsLinkStyled
              href='https://help.tabtouch.com.au/knowledge-base/bonus-offers-terms-conditions/'
              target='_blank'
              rel='noreferrer'
              onClick={() => trackBonusOfferClick(campaign, 'Terms')}
            >
              Terms and Conditions
            </TermsAndConditionsLinkStyled>
          </GridCell>
        </BonusFinePrintRowStyled>
        {shouldShowButton(campaign) && (
          <BonusButtonRowStyled data-tid-bonus-detail-actionbutton>
            <BonusButtonCellStyled valign='middle' align='center'>
              <ButtonBlockStyled
                id='use-bonus'
                type='button'
                color='primary'
                onClick={() => handleCtaClick(campaign)}
              >
                {getButtonText(campaign)}
              </ButtonBlockStyled>
            </BonusButtonCellStyled>
          </BonusButtonRowStyled>
        )}
      </Grid>
    </BetOfferContainerStyled>
  )
}
